/**
 * Webhook chamado quando o operador envia mensagem para o cliente no CRM.
 * Envia apenas a mensagem que o usuário está enviando naquele momento (uma única mensagem),
 * mais identificação do usuário, org, instância, chat e contato (sem histórico de mensagens).
 */

/** URLs de fallback (usadas se o banco não responder). */
export const WEBCHAT_OPERATOR_WEBHOOK_URL   = "https://webhook.fluity.space/webhook/5018cf8e-d97c-48d5-a0f5-f2eb9a50ec4d";
export const INSTAGRAM_OPERATOR_WEBHOOK_URL = "https://webhook.fluity.space/webhook/c8dd12f7-e135-4c2c-92e8-52129f08032c";
export const TELEGRAM_OPERATOR_WEBHOOK_URL  = "https://webhook.fluity.space/webhook/c8dd12f7-e135-4c2c-92e8-52129f08032c";
export const MESSENGER_OPERATOR_WEBHOOK_URL = "https://webhook.fluity.space/webhook/2b2f0ae7-29ec-4601-8e12-889731786233";
export const WHATSAPP_OPERATOR_WEBHOOK_URL  = "https://webhook.fluity.space/webhook/c6e2b841-2523-465c-8a45-baddb65f3c87";

export const OPERATOR_MESSAGE_WEBHOOK_URL = WEBCHAT_OPERATOR_WEBHOOK_URL;

export type OperatorMessageWebhookPayload = {
  event: string;
  user: { id: string | null; email: string | null; name: string };
  org: { id: string; name: string | null } | null;
  instance: { id: string; name: string | null; channel: string | null; status: string | null };
  chat: Record<string, unknown>;
  contact: Record<string, unknown>;
  message: {
    content_message: string;
    message_type: string;
    media_type: string | null;
    media_file_name: string | null;
    media_url: string | null;
    created_at: string;
  };
};

export function buildOperatorMessagePayload(params: {
  userId: string | null;
  userEmail: string | null;
  userName: string;
  orgId: string | undefined;
  orgName: string | null;
  instance: { id: string; name?: string | null; channel?: string | null; status?: string | null };
  chat: Record<string, unknown>;
  contact: Record<string, unknown>;
  message: {
    content_message: string;
    message_type: string;
    media_type?: string | null;
    media_file_name?: string | null;
    media_url?: string | null;
    created_at: string;
  };
  /** Evento enviado ao webhook (ex.: operator_message_to_client, chat_closed). */
  event?: string;
}): OperatorMessageWebhookPayload {
  const { instance, chat, contact, message } = params;
  return {
    event: params.event ?? "operator_message_to_client",
    user: {
      id: params.userId,
      email: params.userEmail,
      name: params.userName || "Operador",
    },
    org: params.orgId
      ? { id: params.orgId, name: params.orgName ?? null }
      : null,
    instance: {
      id: instance.id,
      name: instance.name ?? null,
      channel: instance.channel ?? null,
      status: instance.status ?? null,
    },
    chat,
    contact,
    message: {
      content_message: message.content_message,
      message_type: message.message_type,
      media_type: message.media_type ?? null,
      media_file_name: message.media_file_name ?? null,
      media_url: message.media_url ?? null,
      created_at: message.created_at,
    },
  };
}

/** Resposta esperada do webhook (n8n): contém o message_id da mensagem salva em chat_messages_instagram. */
export type OperatorMessageWebhookResponse = {
  error?: boolean;
  mensagem?: string;
  /** ID da mensagem salva no banco (retorno do n8n; nome vem com typo 'menssage_id'). */
  menssage_id?: string;
};

/** Cliente Supabase para chamar a Edge Function (proxy). Evita CORS e usa a mesma URL do projeto. */
export type OperatorMessageWebhookOptions = {
  supabase: { functions: { invoke: (name: string, opts: { body: unknown }) => Promise<{ data: unknown; error: unknown }> } };
};

/**
 * Envia o payload para o webhook via Edge Function operator-message-webhook-proxy.
 * O proxy chama o webhook.fluity.space no servidor (sem CORS). Sem supabase, não há envio.
 */
export async function sendOperatorMessageToWebhook(
  payload: OperatorMessageWebhookPayload,
  options?: OperatorMessageWebhookOptions
): Promise<OperatorMessageWebhookResponse | null> {
  if (!options?.supabase) {
    console.warn("[operatorMessageWebhook] Supabase não informado; envio ao webhook ignorado.");
    return null;
  }

  try {
    const { data, error } = await options.supabase.functions.invoke("operator-message-webhook-proxy", {
      body: payload,
    });
    if (error) {
      console.warn("[operatorMessageWebhook] Erro ao chamar proxy:", error.message || error);
      return null;
    }
    if (data == null) return null;
    return data as OperatorMessageWebhookResponse;
  } catch (err) {
    console.warn("[operatorMessageWebhook] Erro ao chamar webhook:", err);
    return null;
  }
}
